import { createContext, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AccountAanmakenInput, AuthUser, LoginInput } from '@fleet/shared';
import { setSessionLostHandler } from '../../lib/apiClient';
import { authApi } from './auth.api';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Account aanmaken vanuit een uitnodigingslink. De server logt meteen in, dus
   *  dit zet de gebruiker hier neer zoals `login` dat doet. */
  maakAccount: (token: string, input: AccountAanmakenInput) => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    authApi
      .me()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  /** Alles wat aan de vorige sessie hing weggooien. De react-query-cache staat
   *  vol wagenpark-, personen- en factuurgegevens; die hoort niet te blijven
   *  staan voor wie er na een uitlog achter het scherm zit. */
  const clearSession = useCallback(() => {
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  // Nu de sessie na een uur inactiviteit vervalt, gebeurt dit in gewoon gebruik:
  // een request komt terug met 401 en dan moet de app naar het inlogscherm, in
  // plaats van per pagina een losse foutmelding te tonen.
  useEffect(() => {
    setSessionLostHandler(clearSession);
    return () => setSessionLostHandler(null);
  }, [clearSession]);

  const login = useCallback(async (input: LoginInput) => {
    const { user } = await authApi.login(input);
    setUser(user);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    clearSession();
  }, [clearSession]);

  const maakAccount = useCallback(async (token: string, input: AccountAanmakenInput) => {
    const { user } = await authApi.maakAccount(token, input);
    setUser(user);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, maakAccount }}>
      {children}
    </AuthContext.Provider>
  );
}
